import { Code, ConfirmModal, Input, showToast } from '@lokalise/louis';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type RefObject, createRef, useState } from 'react';

import { isErrorResponse, useCreateBranchMutation } from '@app/api/branch';
import useCmdEnterKeyPress from '@app/hooks/useCmdEnterKeyPress';
import useEffectOnce from '@app/hooks/useEffectOnce';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import {
    Container,
    Content,
    ContentHeading,
    ContentList,
    ContentListItem,
    ContentListItemContent,
    InputWrapper,
} from './styles';
import isBranchNameValid from './utils/isBranchNameValid';

type BranchCreateModalProps = {
    projectId: string;
    opened: boolean;
    onClose: () => void;
    onBranchCreated: (name: string) => void;
};

const BranchCreateModal = ({ opened, projectId, onClose, onBranchCreated }: BranchCreateModalProps) => {
    const [createBranch, { isLoading }] = useCreateBranchMutation();

    const [value, setValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const inputRef: RefObject<HTMLInputElement> = createRef();

    useEffectOnce(() => {
        if (inputRef?.current) {
            inputRef.current.autofocus = true;
            inputRef.current.focus();
        }
    });

    const submitHandler = async (event?: Event) => {
        setErrorMessage('');

        if (!isBranchNameValid(value)) {
            setErrorMessage('Branch name is invalid.');

            return;
        }

        track(AnalyticsEventName.BRANCH_CREATE_CLICKED, {
            action: event?.type === 'click' ? 'click' : 'keypress',
        });

        try {
            await createBranch({ projectId, name: value }).unwrap();
            setValue('');

            onBranchCreated(value);
        } catch (e) {
            const response = e as FetchBaseQueryError | SerializedError;

            if (isErrorResponse(response)) {
                setErrorMessage(response.data.error.message);
                /* istanbul ignore else */
            } else {
                showToast({
                    type: 'error',
                    title: 'Failed to create the branch, please contact support via chat or hello@lokalise.com',
                });
            }
        }
    };

    const submitShortcut = useCmdEnterKeyPress(!opened, submitHandler);

    return (
        <ConfirmModal
            closeText="Cancel"
            confirmText="Create"
            opened={opened}
            title="New branch name"
            tooltipTitle={submitShortcut}
            onClose={onClose}
            onConfirm={submitHandler}
            loading={isLoading}
        >
            <Container>
                <InputWrapper>
                    <Input
                        ref={inputRef}
                        value={value}
                        placeholder="develop"
                        error={errorMessage}
                        onChange={(event) => {
                            setValue(event.currentTarget.value);
                        }}
                    />
                </InputWrapper>
                <Content>
                    <ContentHeading>Branch naming</ContentHeading>
                    <ContentList>
                        <ContentListItem>
                            <ContentListItemContent>must start with a letter or number or dot</ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>must end with a number or letter</ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>
                                <span>may contain numbers, letters and</span> &nbsp; <Code>. - _ /</Code>
                            </ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>
                                multiple consecutive slashes are not allowed
                            </ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>multiple consecutive dots are not allowed</ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>
                                no slash-separated component can begin with a dot
                            </ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>
                                <span>no slash-separated component can end with the sequence</span>
                                &nbsp; <Code>.lock</Code>
                            </ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>min length is 2 characters</ContentListItemContent>
                        </ContentListItem>
                        <ContentListItem>
                            <ContentListItemContent>max length is 100 characters</ContentListItemContent>
                        </ContentListItem>
                    </ContentList>
                </Content>
            </Container>
        </ConfirmModal>
    );
};

export default BranchCreateModal;
